import { useState, useEffect } from "react";
import { Clock, Users, Menu, X } from "lucide-react";
import logoUniversoDigital from "@/assets/logo-universo-digital-64.webp";
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
const navItems = [{
  label: "Inicio",
  href: "#hero"
}, {
  label: "Video",
  href: "#video"
}, {
  label: "Módulos",
  href: "#modules"
}, {
  label: "Bonos",
  href: "#bonus"
}, {
  label: "Testimonios",
  href: "#testimonials"
}, {
  label: "Oferta",
  href: "#offer"
}];
const HeaderBar = () => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  const [soldCount] = useState(97);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const totalCount = 100;
  const progressPercentage = soldCount / totalCount * 100;
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let {
          days,
          hours,
          minutes,
          seconds
        } = prev;
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        return {
          days,
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => item.href.slice(1));
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const formatNumber = (num: number) => num.toString().padStart(2, "0");
  const scrollToSection = (href: string) => {
    const element = document.getElementById(href.slice(1));
    if (element) {
      element.scrollIntoView({
        behavior: "smooth"
      });
    }
    setMobileMenuOpen(false);
  };
  return <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-[hsl(215,12%,40%)] backdrop-blur-md overflow-hidden">
      {/* Background dots pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }} />
      </div>
      
      <div className="w-full px-4 py-2 sm:py-3 relative z-10">
        {/* Desktop Layout */}
        <div className="hidden lg:flex items-center justify-between">
          {/* Logo and Nav */}
          <div className="flex items-center gap-4">
            <img alt="Universo Digital Academy" className="h-10 w-auto flex-shrink-0" width="40" height="40" loading="eager" decoding="async" fetchPriority="high" src="/lovable-uploads/d51b7fb0-af74-4908-aaa1-24a654ab74db.webp" />
            <span className="font-display text-base font-bold text-white whitespace-nowrap hidden xl:block">
              Universo Digital Academy
            </span>
            
            {/* Desktop Navigation */}
            <nav className="flex items-center gap-1">
              {navItems.map(item => <button key={item.href} onClick={() => scrollToSection(item.href)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeSection === item.href.slice(1) ? "bg-gradient-to-r from-primary to-accent text-white" : "text-white/70 hover:text-white hover:bg-white/10"}`}>
                  {item.label}
                </button>)}
            </nav>
          </div>

          {/* Countdown and Counter - Desktop */}
          <div className="flex items-center gap-4">
            {/* Time Countdown */}
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-accent flex-shrink-0" />
              <span className="text-xs font-bold uppercase text-white/90 whitespace-nowrap">
                ¡Oferta termina en:
              </span>
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center rounded-lg bg-accent px-3 py-1.5 min-w-[50px]">
                  <span className="text-2xl font-black tabular-nums leading-none text-accent-foreground">
                    {formatNumber(timeLeft.hours)}
                  </span>
                </div>
                <span className="text-2xl font-bold text-accent">:</span>
                <div className="flex items-center justify-center rounded-lg bg-accent px-3 py-1.5 min-w-[50px]">
                  <span className="text-2xl font-black tabular-nums leading-none text-accent-foreground">
                    {formatNumber(timeLeft.minutes)}
                  </span>
                </div>
                <span className="text-2xl font-bold text-accent">:</span>
                <div className="flex items-center justify-center rounded-lg bg-accent px-3 py-1.5 min-w-[50px]">
                  <span className="text-2xl font-black tabular-nums leading-none text-accent-foreground">
                    {formatNumber(timeLeft.seconds)}
                  </span>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-white/20" />

            {/* Copies Counter */}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="h-2.5 w-20 overflow-hidden rounded-full bg-white/20">
                <div style={{
                width: `${progressPercentage}%`
              }} className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all" />
              </div>
              <span className="text-xs font-medium text-white/80 whitespace-nowrap">
                <span className="font-bold text-accent">{totalCount - soldCount}</span> copias
              </span>
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="flex lg:hidden flex-col gap-3">
          {/* Top row: Logo + Menu button */}
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <img alt="Universo Digital Academy" className="h-8 w-auto" width="32" height="32" loading="eager" decoding="async" fetchPriority="high" src="/lovable-uploads/952945c2-29f2-4b41-88c7-72817a748820.webp" />
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-white">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Bottom row: Countdown + Copies counter */}
          <div className="flex items-center justify-center gap-3 w-full">
            {/* Compact Time Countdown */}
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-accent" />
              <span className="text-xs text-white/70">Oferta termina en:</span>
              <div className="flex items-center gap-0.5">
                <span className="bg-accent px-1.5 py-0.5 rounded text-sm font-bold tabular-nums text-primary">
                  {formatNumber(timeLeft.hours)}
                </span>
                <span className="text-accent font-bold">:</span>
                <span className="bg-accent px-1.5 py-0.5 rounded text-sm font-bold tabular-nums text-primary">
                  {formatNumber(timeLeft.minutes)}
                </span>
                <span className="text-accent font-bold">:</span>
                <span className="bg-accent px-1.5 py-0.5 rounded text-sm font-bold tabular-nums text-primary">
                  {formatNumber(timeLeft.seconds)}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="h-5 w-px bg-white/20" />

            {/* Compact Copies Counter */}
            <div className="flex items-center gap-1.5">
              <Users className="h-4 w-4 text-primary" />
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-16 overflow-hidden rounded-full bg-white/20">
                  <div style={{
                  width: `${progressPercentage}%`
                }} className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all" />
                </div>
                <span className="text-xs font-medium text-white/80">
                  <span className="font-bold text-accent">{totalCount - soldCount}</span> copias
                </span>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && <nav className="mt-3 py-3 border-t border-white/10">
              <div className="flex flex-wrap gap-2 justify-center">
                {navItems.map(item => <button key={item.href} onClick={() => scrollToSection(item.href)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeSection === item.href.slice(1) ? "bg-gradient-to-r from-primary to-accent text-white" : "text-white/70 hover:text-white border border-white/20"}`}>
                    {item.label}
                  </button>)}
              </div>
            </nav>}
        </div>
      </div>
    </header>;
};
export default HeaderBar;